import { useEffect } from 'react';
import { useMyAccountStore } from '../../../stores/useMyAccountStore';

interface NotificationsSectionProps {
  isImpersonating: boolean;
  clientName: string;
  customerServicePhone: string;
}

export function NotificationsSection({
  isImpersonating,
  clientName,
  customerServicePhone,
}: NotificationsSectionProps) {
  const {
    notificationMethods,
    notificationsSubmitting,
    notificationsError,
    notificationsSuccess,
    isInGuardian,
    gv,
    setNotificationMethod,
    setNotificationContact,
    setGvNotify,
    setGvTextAreaCode,
    setGvTextNumber,
    saveNotifications,
    clearNotificationsState,
  } = useMyAccountStore();

  // Clear success/error state each time this section mounts so a stale
  // success banner is never shown on a return visit.
  useEffect(() => {
    clearNotificationsState();
  }, [clearNotificationsState]);

  const methodId = notificationMethods.NotificationMethodId;
  const isText = methodId === 1001 || methodId === '1001';
  const isEmail = methodId === 6 || methodId === '6';
  const isVoice = methodId === 1003 || methodId === '1003';
  const isDnc = methodId === 1000 || methodId === '1000';
  // Consent not required for email or do-not-contact
  const consentNotRequired = isEmail || isDnc;

  const gvNotify = gv.guidedValue?.Preference?.GuidedValueNotify ?? false;

  // "Not in pharmacy systems" condition: user is not in Guardian AND GV fetch found nothing
  const showNotInPharmSys =
    !isInGuardian && gv.gvFetchAttempted && !gv.isUsingGv && !gv.loadingGv;

  // Save is disabled if:
  // - impersonating
  // - submitting
  // - a method is selected that requires consent and consent checkbox not implicit
  // (we use the form's required fields via HTML validation)
  const saveDisabled = isImpersonating || notificationsSubmitting;

  function handleMethodChange(value: number | string) {
    setNotificationMethod(value);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    clearNotificationsState();
    saveNotifications();
  }

  return (
    <div className="account-wrapper">
      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <h1>
            Edit Account <small>Notification Method</small>
          </h1>
          <hr />
        </div>
      </div>

      {gv.loadingGv && !gv.isUsingGv && (
        <div className="gv-spinner">
          <i className="fa fa-spinner fa-spin" style={{ fontSize: 24 }} aria-hidden="true" />{' '}
          Loading Settings...
        </div>
      )}

      {showNotInPharmSys ? (
        <div className="notInPharmSys">
          <span>
            You are currently not in any of our pharmacy systems. If you feel this is an error
            please contact customer support for assistance.
          </span>
        </div>
      ) : (
        <>
          {notificationsSuccess && (
            <div className="alert alert-success" role="alert">
              <strong>Success!</strong> You have updated your notifications.
            </div>
          )}

          {notificationsError && (
            <div className="alert alert-danger" role="alert">
              {notificationsError} Please contact Member Services at {customerServicePhone}.
            </div>
          )}

          <div className="notfication-form-container">
            <h3>Prescription Notification Preferences</h3>
            <h4 className="notification-callToAction">
              How would you like to receive messages from {clientName}?
            </h4>
            <p>
              If {clientName} needs to reach you about a prescription, please indicate your
              preferred method of contact.
            </p>

            <form name="manageNotifications" className="form-horizontal notification-form" onSubmit={handleSubmit}>
              <div className="list-unstyled notification-radios">
                <div className={`radio-container${isText ? ' curr_notify' : ''}`}>
                  <input
                    id="notify-text"
                    type="radio"
                    name="notificationMethod"
                    value={1001}
                    checked={isText}
                    onChange={() => handleMethodChange(1001)}
                  />{' '}
                  Text Message
                </div>
                <div className={`radio-container${isEmail ? ' curr_notify' : ''}`}>
                  <input
                    id="notify-email"
                    type="radio"
                    name="notificationMethod"
                    value={6}
                    checked={isEmail}
                    onChange={() => handleMethodChange(6)}
                  />{' '}
                  Email
                </div>
                <div className={`radio-container${isVoice ? ' curr_notify' : ''}`}>
                  <input
                    id="notify-voice"
                    type="radio"
                    name="notificationMethod"
                    value={1003}
                    checked={isVoice}
                    onChange={() => handleMethodChange(1003)}
                  />{' '}
                  Voice
                </div>
                <div className={`radio-container${isDnc ? ' no_notify' : ''}`}>
                  <input
                    id="notify-dnc"
                    type="radio"
                    name="notificationMethod"
                    value={1000}
                    checked={isDnc}
                    onChange={() => handleMethodChange(1000)}
                  />{' '}
                  Do Not Contact
                </div>
              </div>

              {methodId !== null && methodId !== '' && (
                <div className="col-md-12 notification-fields internal-notifications">
                  {isText && (
                    <div className="col-md-12 notification-group-container">
                      <label className="notication-input" htmlFor="text-area-code">
                        Mobile Number
                      </label>
                      <div className="clearfix" />
                      <input
                        id="text-area-code"
                        className="form-control area-code"
                        type="text"
                        value={notificationMethods.NotificationTextAreaCode}
                        onChange={e =>
                          setNotificationContact('NotificationTextAreaCode', e.target.value)
                        }
                        placeholder="(555)"
                        maxLength={5}
                        required
                      />
                      <input
                        id="text-phone"
                        className="form-control phone-number"
                        type="text"
                        value={notificationMethods.NotificationTextPhoneNumber}
                        onChange={e =>
                          setNotificationContact('NotificationTextPhoneNumber', e.target.value)
                        }
                        placeholder="555-5555"
                        maxLength={8}
                        required
                      />
                    </div>
                  )}

                  {isEmail && (
                    <div className="col-md-12 notification-group-container">
                      <label className="notification-input" htmlFor="notify-email-addr">
                        E-mail Address
                      </label>
                      <div className="clearfix" />
                      <input
                        id="notify-email-addr"
                        className="form-control notify-email"
                        type="email"
                        value={notificationMethods.NotificationEmailAddress}
                        onChange={e =>
                          setNotificationContact('NotificationEmailAddress', e.target.value)
                        }
                        placeholder="ex.me@example.com"
                        required
                      />
                    </div>
                  )}

                  {isVoice && (
                    <div className="col-md-12 notification-group-container">
                      <label className="notication-input" htmlFor="voice-area-code">
                        Voice Number
                      </label>
                      <div className="clearfix" />
                      <input
                        id="voice-area-code"
                        className="form-control area-code"
                        type="text"
                        value={notificationMethods.NotificationVoiceAreaCode}
                        onChange={e =>
                          setNotificationContact('NotificationVoiceAreaCode', e.target.value)
                        }
                        placeholder="(555)"
                        maxLength={5}
                        required
                      />
                      <input
                        id="voice-phone"
                        className="form-control phone-number"
                        type="text"
                        value={notificationMethods.NotificationVoicePhoneNumber}
                        onChange={e =>
                          setNotificationContact('NotificationVoicePhoneNumber', e.target.value)
                        }
                        placeholder="555-5555"
                        maxLength={8}
                        required
                      />
                    </div>
                  )}

                  {/* GuidedValue section */}
                  {gv.isUsingGv && !gv.loadingGv && (
                    <div className="guided-value-section">
                      <h3>myVytlOneLink Preferences</h3>
                      <h4 className="notification-callToAction">
                        Would you like to receive mobile messages related to your drug coverage?
                      </h4>
                      <div className="guided-value-form">
                        <div className="guided-value-toggle">
                          <div className="gv-toggle-text">
                            <p>
                              Occasionally, {clientName} may have a need to contact you with
                              information about your pharmacy benefit. This information may suggest
                              ways you can save money on your medications and/or provide other
                              helpful information. Please indicate your approval to receive these
                              messages on your mobile device. Note, you can opt out at any time.
                            </p>
                          </div>
                          <div className="gv-toggle">
                            <label className="switch" htmlFor="gv-enabled">
                              <input
                                id="gv-enabled"
                                type="checkbox"
                                checked={gvNotify}
                                onChange={e => setGvNotify(e.target.checked)}
                              />
                              <span className="slider round" />
                            </label>
                          </div>
                        </div>

                        {gvNotify && !isText && (
                          <div className="col-md-12 notification-group-container gv-text-number">
                            <label className="notication-input" htmlFor="gv-text-area-code">
                              Mobile Number
                            </label>
                            <div className="clearfix" />
                            <input
                              id="gv-text-area-code"
                              className="form-control area-code"
                              type="text"
                              value={gv.guidedValueTextAreaCode}
                              onChange={e => setGvTextAreaCode(e.target.value)}
                              placeholder="(555)"
                              maxLength={5}
                              required
                            />
                            <input
                              id="gv-text-phone"
                              className="form-control phone-number"
                              type="text"
                              value={gv.guidedValueTextNumber}
                              onChange={e => setGvTextNumber(e.target.value)}
                              placeholder="555-5555"
                              maxLength={8}
                              required
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="clearfix" />

                  {notificationsSubmitting && (
                    <div className="loading-notify">
                      <div className="spinner" style={{ margin: '5px auto 40px auto' }}>
                        <div className="rect1" />
                        {' '}
                        <div className="rect2" />
                        {' '}
                        <div className="rect3" />
                        {' '}
                        <div className="rect4" />
                        {' '}
                        <div className="rect5" />
                        <h4>Updating Notifications</h4>
                      </div>
                    </div>
                  )}

                  {!notificationsSubmitting && (
                    <div className="col-md-12">
                      {!consentNotRequired && (
                        <div className="disclaimer-container">
                          <div className="disclaimer-checkbox">
                            <input
                              type="checkbox"
                              name="consentcheck"
                              id="consentcheck"
                              required
                            />
                          </div>
                          <div className="disclaimer">
                            I consent to receive notifications from {clientName} for my protected
                            healthcare and other services at the phone number(s) above, including my
                            wireless number provided. I understand I may be charged for such
                            notifications by my wireless carrier and that such notifications may be
                            generated by an automated dialing system.
                          </div>
                        </div>
                      )}
                      <div className="notification-button-container">
                        <button
                          type="submit"
                          className="btn btn-primary internal-notification-btn"
                          disabled={saveDisabled}
                        >
                          Save Preferences
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </>
      )}
    </div>
  );
}
